import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useLoaderData, useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { changeSubscriptionBillingAccount, createDraftCatalog, updateDraftCatalog } from '../../common/fetch.js';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import {
  billingAccountChangeCompletedMsg,
  billingAccountChangeFailedMsg,
  cancelMsg,
  confirmMsg,
  deviceListSavedAsDraftMsg,
  selectCompanyHeaderMsg,
  t,
} from '../../common/i18n/index.js';
import { FormGridFieldset } from '../../common/react-hook-form/FormGridFieldset/FormGridFieldset.js';
import { isMultiBiz } from '../../common/utils/accountUtils.js';
import {
  ReceiverTypes,
  createNewBillingAccount,
  getDefaultElectronicOperator,
} from '../../common/utils/billingAccountUtils.js';
import { CreateContactType } from '../../common/utils/contactUtils.js';
import { formatTimestampToDDMMYYYY, getNormalizedAdjustedDate } from '../../common/utils/dateUtils.js';
import { BillingAccount } from '../../generated/api/billingAccount.js';
import { BillingAccountDeliveryMethod } from '../../generated/api/billingAccountDeliveryMethod.js';
import { ContactRole } from '../../generated/api/contactRole';
import { ContactType } from '../../generated/api/contactType.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { BillingAccountFieldset } from '../BillingAccount/BillingAccountFieldset.js';
import { DeliveryMethodFieldset } from '../BillingAccount/DeliveryMethodFieldset.js';
import { CompanySelector } from '../CompanySelector/CompanySelector.js';
import { CreateContactModal } from '../CreateContactModal/CreateContactModal';
import { getUserAccounts } from '../Header/dynamic/headerFunctions.js';
import type { BaLoaderData } from '../../common/loaders.js';
import type { ReceiverType } from '../../common/utils/billingAccountUtils.js';
import type { Catalog } from '../../generated/api/catalog';
import type { Contact } from '../../generated/api/contact.js';
import type { VirtualCatalog } from '../../generated/api/virtualCatalog';

import './BillingAccountForm.scss';

export interface BillingAccountFormProps {
  embeddedMode?: { mdmId?: string };
  onCreated?: (billingAccountId: string) => void;
  onCancel?: () => void;
  onDirtyState?: (isDirty: boolean) => void;
}

export const BillingAccountForm = ({ embeddedMode, onCreated, onCancel, onDirtyState }: BillingAccountFormProps) => {
  const { search, state } = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(search);
  const subscriptionIdQueryParam = searchParams.get('subscriptionId') || undefined;
  const { authenticatedUser } = useAuth();
  const isEmbedded = Boolean(embeddedMode);
  const selectedMdmId = useSearchParams<{ mdmId: string }>().mdmId!;
  const effectiveMdmId = embeddedMode?.mdmId ?? selectedMdmId; // use embedded mdmId if provided
  const { contacts: loaderContacts, billChannels, companyInfo } = useLoaderData() as BaLoaderData;
  const [contacts, setContacts] = useState(loaderContacts);
  const [isSaving, setIsSaving] = useState(false);
  const [createContactModalOpen, setCreateContactModalOpen] = useState(false);
  const [createContactType, setCreateContactType] = useState<CreateContactType>();

  const defaultPayerAddress = companyInfo?.address;
  const defaultOperator = getDefaultElectronicOperator(billChannels);
  const defaultElectronicAddress =
    defaultOperator?.active && defaultOperator?.electronicAddress ? defaultOperator.electronicAddress : '';

  const expirationDate = formatTimestampToDDMMYYYY(getNormalizedAdjustedDate(21).getTime());
  const defaultBillingAccount: BillingAccount = {
    payerBusinessId: companyInfo?.businessId,
    payerName: companyInfo?.companyName ?? '',
    additionalBillReceiverEmails: [],
    billLanguage: 'FI',
    billReceiverEmail: '',
    billReceiverId: state?.defaultBillingContactId,
    billReceiverName: '',
    billReceiverType: ContactType.PERSON,
    billingAccountName: companyInfo?.companyName ?? '',
    billingContactId: state?.defaultBillingContactId,
    billFormatType: BillingAccount.BillFormatTypeEnum.CONSOLIDATED_INVOICE_WITH_ITEMIZATION,
    deliveryMethod: state?.defaultDeliveryMethod ?? BillingAccountDeliveryMethod.ELECTRONIC,
    billElectronicOperator: defaultOperator?.id,
    billElectronicAddress: defaultElectronicAddress,
    payerAddress: {
      line1: defaultPayerAddress?.line1 ?? '',
      postalCode: defaultPayerAddress?.postalCode ?? '',
      postOffice: defaultPayerAddress?.postOffice ?? '',
      countryCode: 'FIN',
    },
  };

  const methods = useForm({
    defaultValues: {
      billingAccount: defaultBillingAccount,
      billReceiverSelection: ReceiverTypes.SAME_AS_CONTACT,
      key: 0,
    },
    values: {
      billingAccount: defaultBillingAccount,
    },
  });
  const { handleSubmit, setValue, watch, formState } = methods;
  const { key } = watch();

  useEffect(() => {
    setContacts(loaderContacts);
  }, [effectiveMdmId, loaderContacts]);

  useEffect(() => {
    if (onDirtyState) {
      onDirtyState(formState.isDirty);
    }
  }, [formState.isDirty, onDirtyState]);

  const handleSubscriptionChange = async (subscriptionId: string, billingAccountId: string) => {
    try {
      await changeSubscriptionBillingAccount(subscriptionId, billingAccountId, effectiveMdmId);
      addNotification(t.CTBU(billingAccountChangeCompletedMsg), 'success');
    } catch {
      addNotification(t.S3DX(billingAccountChangeFailedMsg), 'error');
    }
  };

  const handleCatalogUpdate = async (catalog: Catalog, billingAccountId: string, virtualCatalog: VirtualCatalog) => {
    const updatedCatalog: Catalog = {
      ...catalog,
      billingAccountId,
    };
    try {
      // If we already have a draft, we update it (and keep the published), otherwise we create a new draft
      if (virtualCatalog.draft) {
        await updateDraftCatalog(virtualCatalog.virtualCatalogCode, updatedCatalog, effectiveMdmId);
      } else {
        await createDraftCatalog(virtualCatalog.virtualCatalogCode, updatedCatalog, effectiveMdmId);
      }
      addNotification(t.P7JV(deviceListSavedAsDraftMsg));
    } catch {
      addNotification(t.S3DX(billingAccountChangeFailedMsg), 'error');
    }
  };

  const handleNavigation = (
    redirectPath: string | undefined,
    catalog: Catalog | undefined,
    billingAccountDisplayId: string
  ) => {
    if (redirectPath) {
      navigate(generatePath(redirectPath), {
        replace: true,
        state: { newBillingAccountDisplayId: billingAccountDisplayId },
      });
    } else {
      navigate(
        `${generatePath(paths.BILLING_ACCOUNT, {
          billingAccountId: billingAccountDisplayId,
        })}?mdmId=${effectiveMdmId}`,
        {
          state: {
            showExpirationWarning: true,
            expirationDate,
          },
        }
      );
    }
  };

  const onSubmit = async ({
    billingAccount,
    billReceiverSelection,
  }: {
    billingAccount: BillingAccount;
    billReceiverSelection: ReceiverType;
    key: number;
  }) => {
    setIsSaving(true);
    const newBillingAccount = { ...billingAccount };
    try {
      const createBillingAccountResponse = await createNewBillingAccount({
        newBillingAccount,
        billReceiverSelection,
        contacts: contacts?.contacts || [],
        mdmId: effectiveMdmId,
      });

      addNotification(t.OS99('Billing account was successfully created.'));

      if (onCreated) {
        onCreated(createBillingAccountResponse.billingAccountId);
      }

      if (subscriptionIdQueryParam) {
        await handleSubscriptionChange(subscriptionIdQueryParam, createBillingAccountResponse.billingAccountId);
      } else if (state?.catalog && state.virtualCatalog) {
        await handleCatalogUpdate(
          state.catalog,
          createBillingAccountResponse.billingAccountId,
          state.virtualCatalog as VirtualCatalog
        );
      }
      if (!isEmbedded) {
        handleNavigation(state?.redirectPath, state?.catalog, createBillingAccountResponse.billingAccountDisplayId);
      }
    } catch (err: unknown) {
      const error = err as Error;
      if (!(error instanceof TypeError) && error.message) {
        addNotification(error.message, 'error');
      } else {
        addNotification(t.EBJW('Adding a new billing account failed. Please try again later.'), 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onCancelInternal = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    if (state?.redirectPath) {
      navigate(generatePath(state.redirectPath), {
        replace: true,
      });
    } else {
      navigate(paths.BILLING_ACCOUNTS);
    }
  };

  const openCreateContactModal = (contactType: CreateContactType) => {
    setCreateContactType(contactType);
    setCreateContactModalOpen(true);
  };

  const handleContactCreated = (contact: Contact) => {
    setCreateContactModalOpen(false);
    const isDuplicate = contacts?.contacts?.some(c => c.contactId === contact.contactId);
    if (!isDuplicate) {
      setContacts({
        ...contacts,
        contacts: [...(contacts?.contacts || []), contact],
      });
    }
    if (createContactType === CreateContactType.BILLING) {
      setValue('billingAccount.billingContactId', contact.contactId!, { shouldValidate: true });
    } else if (createContactType === CreateContactType.BILL_RECEIVER) {
      setValue('billingAccount.billReceiverId', contact.contactId!, { shouldValidate: true });
    }
    setValue('key', (key || 0) + 1);
    setCreateContactType(undefined);
  };

  const handleCreateContactModalClose = () => {
    setValue('key', (key || 0) + 1);
    setCreateContactModalOpen(false);
    setCreateContactType(undefined);
  };

  const FormWrapper: 'div' | 'form' = isEmbedded ? 'div' : 'form';
  const formWrapperProps = isEmbedded
    ? {}
    : {
        onSubmit: methods.handleSubmit(onSubmit),
        noValidate: true,
      };

  const handleCompanyChange = (mdmId: string) => {
    methods.setValue('billingAccount.billingContactId', '');
    setValue('key', (key || 0) + 1);
    searchParams.set('mdmId', mdmId);
    navigate(`?${searchParams}`, { state });
  };

  return (
    <>
      {createContactModalOpen && (
        <CreateContactModal
          onClose={handleCreateContactModalClose}
          onContactCreated={handleContactCreated}
          mdmId={effectiveMdmId}
          roles={[ContactRole.BILLING_CONTACT]}
        />
      )}
      <FormProvider {...methods}>
        <FormWrapper {...formWrapperProps}>
          {!isEmbedded && isMultiBiz(authenticatedUser) && !subscriptionIdQueryParam && !state?.catalog && (
            <>
              <h3 className={dsClass.MARGIN_TOP_7}>{t.BR9A(selectCompanyHeaderMsg)}</h3>
              <p>{t.OPKW('Select the company for which you want to create a new billing account.')}</p>
              <FormGridFieldset>
                <CompanySelector
                  className="billing-account-form-company-selector"
                  userAccounts={getUserAccounts(authenticatedUser)}
                  onInputChange={(_, option) => {
                    handleCompanyChange(option.accountMasterId!);
                  }}
                  initialMdmId={selectedMdmId}
                />
              </FormGridFieldset>
            </>
          )}
          {!isEmbedded && !state?.hideDisclaimerText && (
            <CL.Disclaimer
              className={`${dsClass.MARGIN_TOP_6} ${dsClass.MARGIN_BOTTOM_6}`}
              disclaimerType="info"
              icon={<CL.Icon icon="information" />}
              text={t.FI29(
                'Please note that the billing account is valid for 21 days. If no products or services are ' +
                  'ordered for the billing account within this time period, it will be automatically cancelled.'
              )}
              visible
            />
          )}
          {!isEmbedded && <h3>{t.AMRD('Billing account details')}</h3>}
          <BillingAccountFieldset
            billChannels={billChannels}
            contacts={contacts?.contacts || []}
            onCreateBillingContact={() => openCreateContactModal(CreateContactType.BILLING)}
          />
          <DeliveryMethodFieldset
            billChannels={billChannels}
            contacts={contacts?.contacts || []}
            onCreateBillReceiverContact={() => openCreateContactModal(CreateContactType.BILL_RECEIVER)}
          />
          <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_FLEX_END}`}>
            {isEmbedded ? (
              <CL.Button
                type="button"
                className={dsClass.MARGIN_RIGHT_4}
                loading={isSaving}
                onClick={() => handleSubmit(onSubmit)()}
              >
                {t.QVYK(confirmMsg)}
              </CL.Button>
            ) : (
              <CL.Button type="submit" className={dsClass.MARGIN_RIGHT_4} loading={isSaving}>
                {t.QVYK(confirmMsg)}
              </CL.Button>
            )}
            <CL.Button color="light" disabled={isSaving} onClick={onCancelInternal}>
              {t.B2V1(cancelMsg)}
            </CL.Button>
          </div>
        </FormWrapper>
      </FormProvider>
    </>
  );
};
